import React, { useEffect, useState } from 'react';
import './DoorKeys.scss';

interface Holder {
  actorId: number;
  name: string;
}

interface State {
  refrId: number;
  holdName: string;
  label: string;
  holders: Holder[];
}

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'door-keys', ...payload }));
  } catch (e) {
    // Running outside the game (e.g. Storybook)
    // eslint-disable-next-line no-console
    console.log('doorKeys sendMessage', payload);
  }
};

// The panel of a "verrou": a door a Jarl or an Intendant locked with keys, no building behind it
// (client: doorKeysService.ts, server: doorKeySystem.ts). The keys are given to a character who is
// online, by name; the server checks who may do what.
const DoorKeys: React.FC = () => {
  const [state, setState] = useState<State | null>(null);
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [confirmDrop, setConfirmDrop] = useState(false);

  useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail as State;
      setState(detail);
      setLabel(detail.label ?? '');
      setConfirmDrop(false);
    };
    const onClose = () => {
      setState(null);
      setName('');
      setConfirmDrop(false);
    };
    window.addEventListener('skymp5-client:doorKeysState', onState);
    window.addEventListener('skymp5-client:doorKeysClose', onClose);
    return () => {
      window.removeEventListener('skymp5-client:doorKeysState', onState);
      window.removeEventListener('skymp5-client:doorKeysClose', onClose);
    };
  }, []);

  // Escape closes it (the browser has the focus while it is open)
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') send({ action: 'close', refrId: state.refrId });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!state) return null;

  const give = () => {
    const n = name.trim();
    if (!n) return;
    send({ action: 'add', refrId: state.refrId, name: n });
    setName('');
  };

  return (
    <div className="door-keys" role="dialog" aria-label="Verrou">
      <div className="door-keys__panel">
        <div className="door-keys__head">
          <h2 className="door-keys__title">Verrou</h2>
          <button type="button" className="door-keys__close" onClick={() => send({ action: 'close', refrId: state.refrId })}>
            Fermer
          </button>
        </div>
        <p className="door-keys__sub">{state.holdName}</p>

        <label className="door-keys__field">
          <span>Nom de la porte (pour s'y retrouver)</span>
          <input
            value={label}
            maxLength={40}
            placeholder="Ex. Porte de l'arsenal"
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => label !== state.label && send({ action: 'label', refrId: state.refrId, label })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        </label>

        <h3 className="door-keys__h3">Clés ({state.holders.length})</h3>
        <ul className="door-keys__list">
          {state.holders.map((h) => (
            <li key={h.actorId} className="door-keys__row">
              <span className="door-keys__name">{h.name}</span>
              <button
                type="button"
                className="door-keys__btn door-keys__btn--quiet"
                onClick={() => send({ action: 'removeHolder', refrId: state.refrId, actorId: h.actorId })}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>

        <div className="door-keys__add">
          <input
            value={name}
            maxLength={40}
            placeholder="Nom exact d'un joueur connecté"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && give()}
          />
          <button type="button" className="door-keys__btn door-keys__btn--primary" disabled={!name.trim()} onClick={give}>
            Donner une clé
          </button>
        </div>

        <div className="door-keys__foot">
          {confirmDrop ? (
            <>
              <span className="door-keys__warn">La porte s'ouvrira de nouveau à tous. Retirer le verrou ?</span>
              <button
                type="button"
                className="door-keys__btn door-keys__btn--danger"
                onClick={() => send({ action: 'removeLock', refrId: state.refrId })}
              >
                Confirmer
              </button>
              <button type="button" className="door-keys__btn door-keys__btn--quiet" onClick={() => setConfirmDrop(false)}>
                Annuler
              </button>
            </>
          ) : (
            <button type="button" className="door-keys__btn door-keys__btn--danger" onClick={() => setConfirmDrop(true)}>
              Retirer le verrou
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoorKeys;
